import Backbone from 'backbone'
import _ from 'lodash'
import Budget from '../overview/budget'
import Historical from '../overview/historical'
import Notes from 'scoreboard/notes/notes'
import NoteReplyForm from 'scoreboard/notes/reply-form'
import Related from 'scoreboard/related-items/related-items-list'
import Schedule from '../overview/schedule'
import colorIcon from 'scoreboard/components/color-icon'
import formatValue from 'scoreboard/components/format-value'
import initiativeContext from '../overview/initiatives-context'
import template from './initiatives-overview-export-pdf.hbs'

var children = ['export-historical', 'export-schedule', 'export-budget' ]
import config from 'scoreboard/components/config'
import DataType from 'scoreboard/datasets/model/data-type'
const { CURRENCY, PERCENT } = DataType

var InitiativeExport = Backbone.View.extend({

	className: 'export-body',

	initialize: function(options) {

		formatValue.unshortenByDefault()
		this.options = options || {}
		this.model = this.options.model
		this.fullContext = initiativeContext(this.model,
			{replaceWithUnshortened: true})

		var opts = { model: this.model, fullContext: this.fullContext}

		this.schedule = new Schedule({ model: this.model })
		this.budget = new Budget(opts)
		this.historical = new Historical({
			model: this.model,
			mode: this.options.mode,
			legend: false
		})
		this.notes = new Notes(this.fullContext.notes,
			{ section: 'initiatives' })
		this.related = new Related({ model: this.model })
		this.hasRelatedItems = this.related.collection.length > 0

		this.hasRelatedItems && children.push('export-related')

		this.once('export-resize', heightAndWidth => {
			this.historical.trigger('resize', heightAndWidth)
			this.trigger('export-prepared')
		})
	},

	render: function() {

		this.$el.empty().append(template(this._context()))
		// Export page has some styling differences. It looks like
		// scorecard-overview-export-pdf does something similar.
		this.$('.tags-panel.panel').removeClass('panel')
			.addClass('column-row details form')

		_.each(children, className => {
			const childView = this._getChild(className)
			this.$(`.${className}`).empty().append(childView.el)
			childView.render()
		})

		// This has to be .scalable because if there is no table
		// the chart should still be resized
		this.historical.$el.addClass('scalable')
		// Self-reference the correct element inside this div
		// (For chart exports without a table)
		this.historical.$el.data('associated-chart', '.chart-container')

		// #13494 - will add empty page (break) if not removed
		!this.hasRelatedItems && this.$('.export-related').remove()

		// Other things that shouldn't be on the export
		this.historical.$('.add-status').remove()

		this._renderNotes()

		return this
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.noteChildren, 'remove')
		_.each(children, function(child) {
			this._getChild(child).remove()
		}.bind(this))
	},

	/**
	 * build the context object for the hbs template
	 * @private
	 */
	_context: function () {
		var detailKeys = [
			'common.id',
			'initiatives.startDate',
			'initiatives.dueDate',
			'initiatives.endDate',
			'initiatives.percentTimeElapsed',
			'common.currency',
			'initiatives.budget',
			'initiatives.budgetRemaining',
			'common.type',
			'initiatives.goalFunction'
		]
		var detailHash = this.fullContext.pick(detailKeys)
		var details = this._orderFields(detailKeys, detailHash)

		var actualValues = this._getActualValues()

		var ctx = {
			id: config.displayIds ? this.model.id : null,
			label: this.fullContext.label,
			description: this.fullContext.description,
			hasSchedule: this.fullContext.hasSchedule,
			totalBudget: this.fullContext.value('initiatives.budget'),
			details: details,
			assignedUsersAndGroups: this.fullContext.assignedUsersAndGroups,
			hasGraph: this.historical.hasGraph(),
			actualValues: _.isEmpty(actualValues) ? undefined : actualValues,
			expenditures: this.fullContext.expenditures,
			notes: this.fullContext.notes,
			scheduleColorIcon: colorIcon('initiative-schedule'),
			budgetColorIcon: colorIcon('initiative-budget'),
			tags: this.fullContext.tags
		}
		return ctx
	},

	/**
	 * fullContext.pick returns fields in the order they exist in the context,
	 * but for this view we need a different order.  This function returns
	 * them in the order the fields are specified.
	 *
	 * @param fields
	 * @param detailHash
	 * @returns {Array} of Objects with key and value properties
	 * @private
	 */
	_orderFields: function(fields, detailHash) {
		var map = {}
		var details = _.map(detailHash, function(d) {
			map[d.key] = d
		})
		var sorted = []
		_.forEach(fields, function(k) {
			if (map[k]) {
				sorted.push(map[k])
			}
		})
		return sorted
	},

	/**
	 * Initiatives that do not have status updates will roll up those of
	 * their children.  In this case we need to show the actual data points
	 * that are rendered on the charts.  Otherwise, the values are read from
	 * model.statuses
	 *
	 * @return A data model for rendering the actual table values from the chart
	 * @private
	 */
	_getActualValues: function () {
		var currencyCode = this.model.get('currencyCode')
		var budgetFmt = function(v) {
			return formatValue({ dataType: PERCENT, value: v})
		}
		var percentFmt = function(v) {
			return formatValue({
				dataType: CURRENCY, currencyCode: currencyCode, value: v
			})
		}

		var statuses = this.model.get('statuses')
		if (statuses) {
			return _.map(statuses, function (s) {
				return {
					date: s.date,
					percentComplete: budgetFmt(s.percentComplete),
					expendedToDate: percentFmt(s.expendedToDate)
				}
			}.bind(this))
		} else {
			var budget = this.model.get('aggregatedBudget')
			// clone so the merge doesn't affect the model
			var budgetedCosts = _.clone(this.model
				.get('budgetedCostForWorkPerformed'))
			var mergeFunc = function(budgetCost, actualCost) {
				return {
					date: budgetCost.date,
					percentComplete: budgetFmt(budgetCost.value / budget),
					expendedToDate: percentFmt(actualCost.value)
				}
			}
			return _.mergeWith(budgetedCosts, this.model
				.get('actualCostOfWorkPerformed'), mergeFunc)
		}
	},

	/**
	 * makes use of the replyForm template repeatedly to build up all notes
	 * @private
	 */
	_renderNotes: function () {
		this.noteChildren = []
		var noteContainer = this.$('.notes').empty()
		// this.notes is a Backbone collection
		this.notes.forEach(function (n) {
			var note = new NoteReplyForm({
				model: n,
				rootDomainObject: this.model,
				export: true
			})
			note.$el.addClass('panel')
			this.noteChildren.push(note)
			noteContainer.append(note.el)
			note.render()
		}.bind(this))
	},

	/**
	 * Returns the correct child view item when passed it's corresponding
	 * classname.
	 * Ex: _getChild('export-budget') yields this.budget
	 */
	_getChild: function(child) {
		return this[child.substring(child.indexOf('-') + 1)]
	}

})


export default InitiativeExport

InitiativeExport.__filename__ = 'initiatives/export/initiatives-overview-export-pdf.js'
